import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ConfirmationModal from "components/modals/confirmation-modal";
import { PredictiveAddressSearch } from "components/organisations/pav-address-input";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import { PermissionsWrapper } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { IndustryAutoComplete } from "components/shared/industry-auto-complete";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { ViewEditInput } from "components/shared/view-edit-input";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { INDUSTRIES } from "components/user/sign-in/states";
import { GetUserIfLoggedIn, useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { Organisation, Role } from "data/models";
import {
  editOrganisation,
  updateUserRoleOrgMembership,
} from "data/mutations-common";
import {
  fetchOrganisation,
  fetchOrganisationMemberships,
} from "data/queries-common";
import { Form, Formik } from "formik";
import { useState } from "react";
import { resolveIdentityId } from "utils/identity";
import { object, string } from "yup";

const orgCreationValidator = object({
  name: string().required("Organisation name is required"),
  email: string().email("Enter a valid email").required("Email is required"),
  abn: string()
    .matches(/^\d+$/, "ABN must be 11 digits")
    .min(11, "ABN must be 11 digits")
    .max(11, "ABN must be 11 digits")
    .nullable(true),
  address: string().nullable(true),
  phoneNumber: string()
    .matches(
      /^\d+$/,
      "Please provide a valid Australian phone number without the country code",
    )
    .min(
      10,
      "Please provide a valid Australian phone number without the country code",
    )
    .max(
      10,
      "Please provide a valid Australian phone number without the country code",
    )
    .nullable(true),
  industry: string()
    .oneOf([null, ...INDUSTRIES])
    .nullable(true),
  id: string().nullable(true),
});

export const GeneralSettings = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [isTransferOwnershipModalOpen, setIsTransferOwnershipModalOpen] =
    useState(false);
  const [selectedNewOwner, setSelectedNewOwner] = useState("");
  const [confirmationName, setConfirmationName] = useState("");

  const [identity, setIdentity] = useIdentity();
  const identityId = resolveIdentityId(identity);
  const { enqueueMutationFailed, enqueueInfoSnackbar } = useCustomSnackbars();

  const queryClient = useQueryClient();

  const organisationQuery = useQuery({
    queryKey: [QueryKeys.organisation, identityId],
    queryFn: () => fetchOrganisation(),
  });

  const organisationInvitationsQuery = useQuery({
    queryKey: [QueryKeys.organisationInvitations, identityId],
    queryFn: () => fetchOrganisationMemberships(identityId),
  });

  // TODO DS-5307: This feels smelly but can't currently think of a better way to check for permissions loading states
  const { isPermissionsLoading, isPermissionsSuccess } = usePermissions();

  const organisationMutation = useMutation({
    mutationFn: (organisation: Organisation) => editOrganisation(organisation),
    onSuccess: (updatedOrganisation) => {
      queryClient.setQueryData(
        [QueryKeys.organisation, identityId],
        updatedOrganisation,
      );

      setIdentity((prevIdentity) => {
        const user = GetUserIfLoggedIn(prevIdentity);
        if (user) {
          return {
            ...user,
            organisations: user.organisations.map((org) => {
              if (org.active) {
                return {
                  ...org,
                  name: updatedOrganisation.name,
                };
              } else {
                return org;
              }
            }),
          };
        } else {
          return prevIdentity;
        }
      });

      setIsEditing(false);
      setIsEditingContact(false);
    },
  });

  const transferOwnershipMutation = useMutation({
    mutationFn: ({
      organisationId,
      memberUserId,
    }: {
      organisationId: string;
      memberUserId: string;
    }) => updateUserRoleOrgMembership(Role.OWNER, memberUserId, organisationId),
    onSuccess: () => {
      // Get organization and new owner details for the success message
      const orgName = organisationQuery.data?.name;
      const newOwner = organisationInvitationsQuery.data?.find(
        (member) => member.userMemberId === selectedNewOwner,
      );
      const newOwnerName = newOwner?.userFullName || newOwner?.userEmail;

      // Show success snackbar
      enqueueInfoSnackbar(
        `Ownership of ${orgName} has been successfully transferred to ${newOwnerName}. Your role has been updated to Admin.`,
        "success",
      );

      // Close the modal and reset state
      setIsTransferOwnershipModalOpen(false);
      setSelectedNewOwner("");
      setConfirmationName("");

      // Invalidate and refetch relevant queries
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.organisationInvitations, identityId],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.permissions, identityId],
      });

      // Optionally refresh identity context to reflect ownership change
      // The user might be redirected or lose access after ownership transfer
    },
    onError: (error: Error) => {
      // Show error snackbar and keep modal open so user can try again
      enqueueMutationFailed(error.message);
    },
  });

  const isLoading =
    organisationQuery.isPending ||
    organisationInvitationsQuery.isPending ||
    isPermissionsLoading;
  const isSuccess =
    organisationQuery.isSuccess &&
    organisationInvitationsQuery.isSuccess &&
    isPermissionsSuccess;

  return (
    <>
      {/* TODO DS-5307: Add the mutations here if appropriate */}
      <ReactQueryErrorWrapper
        queries={[organisationQuery, organisationInvitationsQuery]}
      />

      {isLoading && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <CardHeader title="Organisation Profile" />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height="321px" />
          </Grid>
          <Grid item xs={12} md={6}>
            <CardHeader title="Contact Details" />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height="225px" />
          </Grid>
        </Grid>
      )}

      {isSuccess && (
        <>
          <Formik<Organisation>
            enableReinitialize={true}
            initialValues={{
              name: organisationQuery.data.name,
              id: organisationQuery.data.id,
              email: organisationQuery.data.email,
              address: organisationQuery.data.address,
              industry: organisationQuery.data.industry,
              phoneNumber: organisationQuery.data.phoneNumber,
              abn: organisationQuery.data.abn,
              orgSize: organisationQuery.data.orgSize,
              hasUsedTrial: organisationQuery.data.hasUsedTrial,
            }}
            onSubmit={async (values) => {
              organisationMutation.mutate({
                id: organisationQuery.data?.id,
                ...values,
              });
            }}
            validationSchema={orgCreationValidator}
          >
            {({
              values,
              handleChange,
              isSubmitting,
              setFieldValue,
              handleBlur,
              errors,
              touched,
              resetForm,
              isValid,
              dirty,
            }: any) => (
              <Form>
                {/* Organisation Profile Row */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <CardHeader title="Organisation Profile" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Box
                          gap={1}
                          display="flex"
                          flexDirection="column"
                          marginBottom={isEditing ? 0 : 1}
                        >
                          <ViewEditInput isEditing={isEditing}>
                            <TextField
                              id="name"
                              name="name"
                              label="Organisation Name"
                              type="text"
                              margin="normal"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.name && Boolean(errors.name)}
                              value={values.name}
                              variant="outlined"
                              fullWidth
                              size="small"
                              helperText={touched.name && errors.name}
                              inputProps={{ maxLength: 120 }}
                              sx={{
                                padding: "0px",
                                margin: "0px",
                                marginTop: 1,
                              }}
                            />
                          </ViewEditInput>
                          <ViewEditInput isEditing={isEditing}>
                            <TextField
                              id="id"
                              size="small"
                              label="Organisation Id"
                              name="id"
                              value={values.id}
                              error={touched.id && Boolean(errors.id)}
                              helperText={touched.id && errors.id}
                              disabled
                            />
                          </ViewEditInput>
                          <ViewEditInput isEditing={isEditing}>
                            <IndustryAutoComplete
                              size="small"
                              label="Industry"
                              value={values.industry}
                              onChange={handleChange}
                              setFieldValue={setFieldValue}
                              readOnly={
                                !isEditing ||
                                organisationQuery.isPending ||
                                isSubmitting
                              }
                              disabled={
                                !isEditing ||
                                organisationQuery.isPending ||
                                isSubmitting
                              }
                              error={
                                touched.industry !== null &&
                                touched.industry &&
                                Boolean(errors.industry)
                              }
                              helperText={touched.industry && errors.industry}
                            />
                          </ViewEditInput>
                          <ViewEditInput
                            isEditing={isEditing}
                            label="Organisation Size"
                            value={values.orgSize}
                          >
                            <FormControl fullWidth>
                              <InputLabel id="org-size-select-label">
                                Organisation Size
                              </InputLabel>
                              <Select
                                labelId="org-size-select-label"
                                id="orgSize"
                                name="orgSize"
                                value={values.orgSize}
                                label="Organisation Size"
                                onChange={handleChange}
                                size="small"
                              >
                                <MenuItem value="1-10 employees">
                                  1-10 employees (Micro business/Startup)
                                </MenuItem>
                                <MenuItem value="11-50 employees">
                                  11-50 employees (Small business)
                                </MenuItem>
                                <MenuItem value="51-200 employees">
                                  51-200 employees (Mid-sized business)
                                </MenuItem>
                                <MenuItem value="201-500 employees">
                                  201-500 employees (Large mid-market)
                                </MenuItem>
                                <MenuItem value="501-1,000 employees">
                                  501-1,000 employees (Small enterprise)
                                </MenuItem>
                                <MenuItem value="1,001-5,000 employees">
                                  1,001-5,000 employees (Mid enterprise)
                                </MenuItem>
                                <MenuItem value="5,001-10,000 employees">
                                  5,001-10,000 employees (Large enterprise)
                                </MenuItem>
                                <MenuItem value="10,000+ employees">
                                  10,000+ employees (Global enterprise)
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </ViewEditInput>
                          <ViewEditInput isEditing={isEditing}>
                            <TextField
                              id="abn"
                              size="small"
                              label="ABN"
                              name="abn"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.abn}
                              inputProps={{ maxLength: 11 }}
                              error={touched.abn && Boolean(errors.abn)}
                              helperText={touched.abn && errors.abn}
                            />
                          </ViewEditInput>
                        </Box>
                      </CardContent>
                      <CardActions
                        sx={{
                          justifyContent: "flex-end",
                        }}
                      >
                        {!isEditing && (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => setIsEditing(true)}
                          >
                            Edit
                          </Button>
                        )}
                        {isEditing && (
                          <Box display="flex" flexDirection="row" gap={2}>
                            <Button
                              size="small"
                              onClick={() => {
                                resetForm();
                                setIsEditing(false);
                              }}
                              disabled={organisationMutation.isPending}
                            >
                              Cancel
                            </Button>
                            <LoadingButton
                              loading={organisationMutation.isPending}
                              size="small"
                              variant="contained"
                              type="submit"
                              disabled={!isValid || !dirty}
                            >
                              Save
                            </LoadingButton>
                          </Box>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>

                  {/* Transfer Ownership Row */}
                  <PermissionsWrapper
                    keys={[PermissionsKeys.membersChangeOwner]}
                    messages={{
                      [PermissionsReasons.ROLE_DENY]: <></>,
                    }}
                  >
                    <Grid item xs={12} md={6}>
                      <CardHeader title="Transfer Ownership" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography
                            sx={{ marginLeft: "14px", marginRight: "14px" }}
                          >
                            Permanently transfer ownership of this organisation
                            to another member. This action cannot be undone.
                          </Typography>
                        </CardContent>
                        <CardActions
                          sx={{
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() =>
                              setIsTransferOwnershipModalOpen(true)
                            }
                          >
                            Transfer Ownership
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  </PermissionsWrapper>

                  {/* Contact Details Row */}
                  <Grid item xs={12} md={6}>
                    <CardHeader title="Contact Details" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Box
                          gap={1}
                          display="flex"
                          flexDirection="column"
                          marginBottom={isEditingContact ? 0 : 1}
                        >
                          <ViewEditInput isEditing={isEditingContact}>
                            <TextField
                              id="email"
                              size="small"
                              label="Email"
                              name="email"
                              type="email"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.email}
                              error={touched.email && Boolean(errors.email)}
                              helperText={touched.email && errors.email}
                              sx={{
                                padding: "0px",
                                margin: "0px",
                                marginTop: 1,
                              }}
                            />
                          </ViewEditInput>
                          <ViewEditInput isEditing={isEditingContact}>
                            <TextField
                              id="phoneNumber"
                              size="small"
                              label="Phone"
                              name="phoneNumber"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.phoneNumber}
                              error={
                                touched.phoneNumber &&
                                Boolean(errors.phoneNumber)
                              }
                              helperText={
                                touched.phoneNumber && errors.phoneNumber
                              }
                            />
                          </ViewEditInput>
                          <ViewEditInput isEditing={isEditingContact}>
                            <PredictiveAddressSearch
                              disabled={
                                organisationQuery.isPending || !isEditingContact
                              }
                              readOnly={
                                organisationQuery.isPending || !isEditingContact
                              }
                              textFieldStyle={{
                                marginTop: "0px",
                                marginBottom: "0px",
                              }}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              setFieldValue={setFieldValue}
                              id="address"
                              size="small"
                              name="address"
                              label="Address"
                              value={values.address}
                              error={
                                touched.address !== null &&
                                touched.address &&
                                Boolean(errors.address)
                              }
                              helperText={touched.address && errors.address}
                            />
                          </ViewEditInput>
                        </Box>
                      </CardContent>
                      <CardActions
                        sx={{
                          justifyContent: "flex-end",
                        }}
                      >
                        {!isEditingContact && (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => setIsEditingContact(true)}
                          >
                            Edit
                          </Button>
                        )}
                        {isEditingContact && (
                          <Box display="flex" flexDirection="row" gap={2}>
                            <Button
                              size="small"
                              onClick={() => {
                                resetForm();
                                setIsEditingContact(false);
                              }}
                              disabled={organisationMutation.isPending}
                            >
                              Cancel
                            </Button>
                            <LoadingButton
                              loading={organisationMutation.isPending}
                              size="small"
                              variant="contained"
                              type="submit"
                              disabled={!isValid || !dirty}
                            >
                              Save
                            </LoadingButton>
                          </Box>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>

          <ConfirmationModal
            open={isTransferOwnershipModalOpen}
            handleClose={() => {
              setIsTransferOwnershipModalOpen(false);
              setSelectedNewOwner("");
              setConfirmationName("");
            }}
            header="Transfer Ownership"
            body=""
            leftButtonText="Cancel"
            rightButtonText="Transfer Ownership"
            leftButtonColor="primary"
            rightButtonColor="error"
            leftButtonVariant="outlined"
            rightButtonVariant="contained"
            handleLeftButton={() => {
              setIsTransferOwnershipModalOpen(false);
              setSelectedNewOwner("");
              setConfirmationName("");
            }}
            handleRightButton={() => {
              if (organisationQuery.data?.id && selectedNewOwner) {
                transferOwnershipMutation.mutate({
                  organisationId: organisationQuery.data.id,
                  memberUserId: selectedNewOwner,
                });
              }
            }}
            rightButtonDisabled={
              !selectedNewOwner ||
              confirmationName.trim() !==
                organisationQuery.data?.name?.trim() ||
              transferOwnershipMutation.isPending
            }
            isLoading={transferOwnershipMutation.isPending}
          >
            <Box
              sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}
            >
              <Typography>
                This is a permanent and irreversible action. The new owner will
                have <b>full, unrestricted control</b> over the{" "}
                {organisationQuery.data.name} organisation.
              </Typography>
              <Typography>
                Upon successful transfer your role will be immediately changed
                to <b>admin</b>.
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel id="member-select-label">New Owner</InputLabel>
                <Select
                  labelId="member-select-label"
                  id="member-select"
                  value={selectedNewOwner}
                  label="New Owner"
                  onChange={(e) => setSelectedNewOwner(e.target.value)}
                  disabled={organisationInvitationsQuery.isPending}
                >
                  {organisationInvitationsQuery.data
                    ?.filter((member) => {
                      const user = GetUserIfLoggedIn(identity);
                      // Filter out the current user and pending members
                      return (
                        user &&
                        member.userMemberId !== user.cognitoSub &&
                        member.status === "Accepted"
                      );
                    })
                    ?.map((member) => (
                      <MenuItem
                        key={member.userMemberId}
                        value={member.userMemberId}
                      >
                        {member.userFullName ||
                          `${member.userFirstName} ${member.userLastName}`}{" "}
                        ({member.userEmail || member.organisationEmail})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <Typography>
                To confirm, please type your organisation's name
                <br />
                <b>{organisationQuery.data.name}</b>
              </Typography>

              <TextField
                size="small"
                label={`Type "${organisationQuery.data?.name}" to confirm`}
                value={confirmationName}
                onChange={(e) => setConfirmationName(e.target.value)}
                placeholder="Organisation name"
                fullWidth
              />
            </Box>
          </ConfirmationModal>
        </>
      )}
    </>
  );
};
